import { memo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { CheckIcon } from '@air/design-icons';
import {
  contentCollectionAllUploadsCompletedSelector,
  contentCollectionFormIsValidSelector,
  contentCollectionRequiredFieldsSelector,
} from '@air/feature-content-collection/redux';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useToasts } from '@air/provider-toast';
import { cn } from '@air/utils-tailwind';

import { ContentCollectionActionBar } from '~/components/ContentCollection/ContentCollectionActionBar';
import { ContentCollectionBanner } from '~/components/ContentCollection/ContentCollectionBanner';
import { ContentCollectionGalleryView } from '~/components/ContentCollection/ContentCollectionGalleryView';
import { ContentCollectionManageViewSidebar } from '~/components/ContentCollection/ContentCollectionManageViewSidebar';
import { ContentCollectionUploadMoreButton } from '~/components/ContentCollection/ContentCollectionUploadMoreButton';
import { ContentCollectionUploadsTrackingPanel } from '~/components/ContentCollection/ContentCollectionUploadsTrackingPanel';

export const ContentCollectionManageView = memo(() => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const mobileScrollElementRef = useRef<HTMLDivElement>(null);
  const desktopScrollElementRef = useRef<HTMLDivElement>(null);
  const hasShownReadyToast = useRef(false);
  const requiredFields = useSelector(contentCollectionRequiredFieldsSelector);
  const formIsValid = useSelector(contentCollectionFormIsValidSelector);
  const allUploadsCompleted = useSelector(contentCollectionAllUploadsCompletedSelector);
  const { showToast } = useToasts();

  useEffect(() => {
    if (!allUploadsCompleted || !formIsValid) {
      hasShownReadyToast.current = false;
    } else if (isAboveMediumScreen && requiredFields.length && !hasShownReadyToast.current) {
      hasShownReadyToast.current = true;
      showToast(
        <div className="flex items-center gap-2">
          <CheckIcon size="sm" />
          <span>All files are ready to be submitted!</span>
        </div>,
        { color: 'green' },
      );
    }
  }, [isAboveMediumScreen, allUploadsCompleted, requiredFields.length, formIsValid, showToast]);

  return (
    <div
      className={cn(
        'flex size-full flex-col overflow-y-auto md:flex-row md:overflow-y-hidden md:pb-0',
        requiredFields.length && allUploadsCompleted ? 'pb-24' : 'pb-44',
      )}
      ref={mobileScrollElementRef}
      data-testid="CONTENT_COLLECTION_MANAGE_VIEW"
    >
      <ContentCollectionBanner className="md:hidden" />
      <ContentCollectionManageViewSidebar />
      <div className="relative flex w-full flex-col">
        <ContentCollectionActionBar />
        <div className="w-full md:overflow-y-auto md:px-3" ref={desktopScrollElementRef}>
          <ContentCollectionBanner className="hidden md:block" />
          <ContentCollectionGalleryView
            scrollElementRef={isAboveMediumScreen ? desktopScrollElementRef : mobileScrollElementRef}
          />
        </div>

        {isAboveMediumScreen && (
          <ContentCollectionUploadsTrackingPanel className="absolute bottom-4 left-1/2 -translate-x-1/2" />
        )}
      </div>
      <ContentCollectionUploadMoreButton className="mx-4 md:hidden">Add more files</ContentCollectionUploadMoreButton>
    </div>
  );
});

ContentCollectionManageView.displayName = 'ContentCollectionManageView';
